import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRightIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { getArticleBySlug, getRelatedArticles } from '../data/articles';
import { ArticleBody } from '../components/article/ArticleBody';
import { ArticleCTABand } from '../components/article/ArticleCTABand';
import { ArticleFAQ } from '../components/article/ArticleFAQ';
import { ArticleTOC } from '../components/article/ArticleTOC';
import { usePageMeta } from '../hooks/usePageMeta';
import { buildMatchLink } from '../utils/matchParams';
const FAQ_ID = 'faqs';
export function ArticlePage() {
  const {
    articleName
  } = useParams<{
    articleName: string;
  }>();
  const location = useLocation();
  // Each article also has an explicit route so it appears as its own URL, and
  // those routes carry no `:articleName` param — fall back to the path.
  const slug = articleName || location.pathname.split('/').filter(Boolean).pop() || '';
  const article = getArticleBySlug(slug);
  usePageMeta(article?.metaTitle || 'Article | Online College Guide', article?.metaDescription || 'Compare accredited online degree programs. Get matched by career goals, schedule, and budget.', {
    ogType: 'article'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!article) {
    return <main className="min-h-[60vh] flex items-center justify-center px-4 py-20" data-id="element-875" data-mp="2htxm42gdr54j">
        <div className="text-center max-w-md" data-id="element-876" data-mp="1u6pjjsac6a67">
          <h1 className="text-2xl font-bold text-navy mb-3" data-id="element-877" data-mp="1p7wgktsoe1ka">
            Article Not Found
          </h1>
          <p className="text-gray-600 mb-6" data-id="element-878" data-mp="22zb1x7a22eui">
            We couldn't find that article. Browse our guides or get matched with
            accredited programs instead.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white font-bold py-3 px-7 rounded-lg text-sm transition-colors duration-200" data-id="element-879" data-mp="1aigonaherq30">
            Get Matched Now
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-880" data-mp="35l3mks094ieq" />
          </Link>
        </div>
      </main>;
  }
  const related = getRelatedArticles(article.slug);
  const midpoint = Math.ceil(article.sections.length / 2);
  const matchHref = buildMatchLink('/request-information', article.match);
  return <main className="bg-white" data-id="element-881" data-mp="3kcm0mk9jw2vg">
      {/* Hero */}
      <header className="hero-bg" data-id="element-882" data-mp="2mnkfvv19nbvh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-883" data-mp="2molprw6jpsbm">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-884" data-mp="2rmn9mjbspvo5">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-885" data-mp="2wl87ic3ks39f">
              <li data-id="element-886" data-mp="31k1nqca8y8da">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-887" data-mp="31kkvj066ngdl">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-888" data-mp="bu7qg3p2rhrq">/</li>
              <li data-id="element-889" data-mp="gtsugsj3qgbb">
                <Link to="/articles" className="hover:text-white transition-colors" data-id="element-890" data-mp="gtav3n157lz6">
                  Articles
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-891" data-mp="1wcib60q7xfvj">/</li>
              <li className="text-white/90" data-id="element-892" data-mp="1wev233dyf0kq">{article.category}</li>
            </ol>
          </nav>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gold mb-4" data-id="element-893" data-mp="1rk784p06ldyr">
            {article.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-4xl" data-id="element-894" data-mp="1hnm7sgz3wr19">
            {article.title}
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl mb-6" data-id="element-895" data-mp="1dkz85tw721ti">
            {article.intro}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/60" data-id="element-896" data-mp="18p0q0o1glg1j">
            <span className="inline-flex items-center gap-1.5" data-id="element-897" data-mp="13q78mkj1x4w3">
              <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-898" data-mp="32zw05usal36f" />
              Updated {article.updated}
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-899" data-mp="2y015dg5ebtg3">
              <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-900" data-mp="1vq0ymb5biefu" />
              {article.readMinutes} min read
            </span>
          </div>
        </div>
      </header>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-901" data-mp="1lpgvf2y8mzuz">
        <div className="flex flex-col lg:flex-row gap-12" data-id="element-902" data-mp="1qp3e3rwu58b6">
          <article className="lg:w-2/3 space-y-12" data-id="element-903" data-mp="1qrfwu7af3mpa">
            <ArticleBody sections={article.sections} interruptAfter={midpoint} interruption={<ArticleCTABand variant="inline" body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-905" data-mp="1gtjp7pgov9vx" />} data-id="element-904" data-mp="1bznrmo4c7p1a" />
            <ArticleFAQ id={FAQ_ID} faqs={article.faqs} data-id="element-906" data-mp="t1kz3zckz9yq" />
          </article>

          {/* Sidebar */}
          <aside className="lg:w-1/3 space-y-8" data-id="element-907" data-mp="xzw7s6r53efd">
            <div className="lg:sticky lg:top-24 space-y-8" data-id="element-908" data-mp="y2s6hxata74v">
              <div className="bg-navy rounded-xl p-6" data-id="element-909" data-mp="efxbu5b2zpbn">
                <h2 className="text-lg font-bold text-white mb-2" data-id="element-910" data-mp="adtytc8rkco9">
                  Get Matched Now
                </h2>
                <p className="text-sm text-white/75 leading-relaxed mb-5" data-id="element-911" data-mp="odljtwxp6tbr">
                  Answer a few questions and we'll connect you with accredited
                  programs that fit your goals, schedule, and budget.
                </p>
                <Link to={matchHref} className="block text-center bg-cta hover:bg-cta-dark text-white font-bold py-3 px-5 rounded-lg text-sm transition-colors duration-200" data-id="element-912" data-mp="xc6vr98nza3k">
                  {article.cta.buttonLabel}
                </Link>
                <p className="mt-3 text-xs text-white/55 text-center" data-id="element-913" data-mp="125i7nosc2h2c">
                  No cost to students. No obligation.
                </p>
              </div>

              <ArticleTOC sections={article.sections} faqId={FAQ_ID} data-id="element-914" data-mp="1c3pdpcl33epk" />

              <div className="border-t border-gray-200 pt-6" data-id="element-915" data-mp="1j6e095thtiqj">
                <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-4" data-id="element-916" data-mp="1j7xhaymz3xtd">
                  Keep Reading
                </h2>
                <ul className="space-y-4" data-id="element-917" data-mp="1t3s8ii59j17r">
                  {article.internalLinks.map(link => <li key={link.anchor} data-id="element-918" data-mp="1xva5yw82ujnz">
                      <Link to={buildMatchLink(link.to, link.match ?? article.match)} className="group flex items-start gap-2 text-sm text-gray-700 hover:text-navy transition-colors duration-150" data-id="element-919" data-mp="1xwblbcw4lsbi">
                        <ArrowRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400 group-hover:text-navy transition-colors duration-150" aria-hidden="true" data-id="element-920" data-mp="2guec9x88hzby" />
                        <span className="capitalize" data-id="element-921" data-mp="mg3pdub79f8t">{link.anchor}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Related articles */}
      {related.length > 0 && <section aria-labelledby="related-heading" className="bg-light border-t border-gray-200 py-14" data-id="element-922" data-mp="54ew2y7u6hw8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-923" data-mp="xuwwfrh6e55y">
            <h2 id="related-heading" className="text-2xl font-bold text-navy mb-6" data-id="element-924" data-mp="xt3z52sgcxam">
              Related Guides
            </h2>
            <ul className="grid sm:grid-cols-3 gap-5" data-id="element-925" data-mp="nvpip36j48ib">
              {related.map(item => <li key={item.slug} className="flex" data-id="element-926" data-mp="ix4kf9au7ywi">
                  <Link to={`/article/${item.slug}`} className="group bg-white border border-gray-200 rounded-xl p-6 flex flex-col w-full hover:border-navy transition-colors duration-200" data-id="element-927" data-mp="38mztna973laz">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-928" data-mp="3taplam9febsj">
                      {item.category}
                    </span>
                    <span className="font-bold text-gray-900 leading-snug mb-3" data-id="element-929" data-mp="3kce04zrbw67h">
                      {item.title}
                    </span>
                    <span className="mt-auto inline-flex items-center text-sm font-semibold text-navy" data-id="element-930" data-mp="3ofa9dzuzapyr">
                      Read guide
                      <ArrowRightIcon className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform duration-200" aria-hidden="true" data-id="element-931" data-mp="kw7h4vmlvge7" />
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>
        </section>}

      <ArticleCTABand body={article.cta.body} buttonLabel={article.cta.buttonLabel} match={article.match} data-id="element-932" data-mp="yu5k74vh7qee" />
    </main>;
}